import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * Add to Home Screen (DSPRT-185), phones and tablets only.
 *
 * Android (Chrome, Edge, Samsung Internet) hands the page a
 * `beforeinstallprompt` event it can fire from its own button. iPhone and iPad
 * have no such API: the only way in is Share, then Add to Home Screen, so the
 * page can only show how. Both are reached from the same store, which main.tsx
 * imports at boot because the Android event fires once, early, and a lazy
 * chunk (the UFC desk) would miss it.
 */

interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "statsummit:a2hs-dismissed";
/** A "no thanks" holds for a month, then the card may ask once more. */
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

let deferred: InstallEvent | null = null;
let installed = false;
let helpOpen = false;
let version = 0;
const listeners = new Set<() => void>();
const emit = () => { version++; listeners.forEach((l) => l()); };

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Hold it: the browser's own mini-bar would otherwise show at its own moment.
    e.preventDefault();
    deferred = e as InstallEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    helpOpen = false;
    emit();
  });
}

function isStandalone(): boolean {
  return (navigator as unknown as { standalone?: boolean }).standalone === true
    || matchMedia("(display-mode: standalone)").matches;
}

/**
 * iPhone, iPad (which reports as a Mac, with a touch screen) and iPod, in a
 * real browser. An app's own web view (Facebook, Instagram, ...) has no Share
 * sheet entry for this, so it gets nothing.
 */
function isIosBrowser(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return ios && !/FBAN|FBAV|Instagram|Line\/|Twitter|Snapchat|MicroMessenger|GSA\//.test(ua);
}

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return at > 0 && Date.now() - at < DISMISS_MS;
  } catch {
    return false;
  }
}

/** A touch device at tab-bar width. A desktop window narrowed to a phone is not one. */
const MOBILE_QUERY = "(max-width: 860px) and (pointer: coarse)";

export interface Install {
  /** "native": one tap installs. "ios": it has to be shown how. null: nothing to offer. */
  kind: "native" | "ios" | null;
  /** The card is up on its own: eligible, and not turned away within the month. */
  showCard: boolean;
  /** Asked for from the More menu, so it opens even after a "no thanks". */
  helpOpen: boolean;
  /** Fires the Android prompt; on iPhone, opens the how-to. */
  start: () => void;
  dismiss: () => void;
  closeHelp: () => void;
}

export function useInstall(): Install {
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => version,
    () => 0,
  );
  const [mobile, setMobile] = useState(() => matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = matchMedia(MOBILE_QUERY);
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const eligible = mobile && !installed && !isStandalone();
  const kind = !eligible ? null : isIosBrowser() ? "ios" : deferred ? "native" : null;

  const start = () => {
    if (kind === "ios") { helpOpen = true; emit(); return; }
    const ev = deferred;
    if (!ev) return;
    // An event can be used once, accepted or not; Chrome fires a new one if
    // the visitor turned it down and the page is still eligible later.
    deferred = null;
    emit();
    void ev.prompt().then(() => ev.userChoice).then((c) => {
      if (c.outcome === "dismissed") rememberDismissal();
    }).catch(() => {});
  };

  return {
    kind,
    showCard: kind !== null && !dismissedRecently(),
    helpOpen: kind === "ios" && helpOpen,
    start,
    dismiss: () => { rememberDismissal(); helpOpen = false; emit(); },
    closeHelp: () => { helpOpen = false; emit(); },
  };
}

function rememberDismissal() {
  try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* private mode */ }
  emit();
}
